import { kMedSummaryApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'
import { MedSummaryEntry, MedSummaryEntrySchema, MedSummaryEntryUpdate, MedSummaryEntryUpdateSchema } from '@/medical-billing-summaries/entries/store/schemas'

export interface UpdateMedSummaryAssetEntryParams {
  assetId: string
  entryId: string
  updates: MedSummaryEntryUpdate
}

/**
 * Applies one user edit to a live billing summary entry.
 */
export async function updateMedSummaryAssetEntry(params: UpdateMedSummaryAssetEntryParams): Promise<MedSummaryEntry> {
  const { assetId, entryId, updates } = params
  const sentry_transaction_id = nanoid()

  try {
    // Validate update schema
    const validatedUpdates = MedSummaryEntryUpdateSchema.parse(updates)

    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'PUT',
      body: JSON.stringify(validatedUpdates),
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMedSummaryApiBasePath}/asset/${assetId}/entries/${entryId}`
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    const parsedData = MedSummaryEntrySchema.parse(data)
    return parsedData
  } catch (error) {
    if (!(error instanceof ApiError)) {
      // Skip api errors already captured above
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
        })
      })
    }
    throw error
  }
}
